import { readBannerOffsetPx, readSafeAreaBottomPx } from './walkthroughLayout';

const VIEWPORT_PADDING = 16;
const TOOLTIP_GAP = 12;

/**
 * @returns {{ top: number, right: number, bottom: number, left: number }}
 */
export function getWalkthroughViewportInsets() {
  return {
    top: VIEWPORT_PADDING + readBannerOffsetPx(),
    right: VIEWPORT_PADDING,
    bottom: VIEWPORT_PADDING + readSafeAreaBottomPx(),
    left: VIEWPORT_PADDING,
  };
}

/**
 * @param {import('../walkthroughTypes').ResolvedWalkthroughStep} step
 * @param {boolean} isMobile
 * @returns {string | null}
 */
export function resolveTargetSelector(step, isMobile) {
  if (!step) return null;
  if (isMobile && step.mobileSelector) return step.mobileSelector;
  if (!isMobile && step.desktopSelector) return step.desktopSelector;
  return step.targetSelector ?? null;
}

/**
 * @param {string | undefined} placement
 * @returns {'top' | 'bottom' | 'left' | 'right'}
 */
export function resolvePreferredPlacement(placement) {
  if (placement === 'top' || placement === 'left' || placement === 'right') {
    return placement;
  }
  return 'bottom';
}

/**
 * @param {'top' | 'bottom' | 'left' | 'right'} preferred
 * @returns {Array<'top' | 'bottom' | 'left' | 'right'>}
 */
function placementFallbackOrder(preferred) {
  const all = ['bottom', 'top', 'right', 'left'];
  return [preferred, ...all.filter((p) => p !== preferred)];
}

/**
 * @param {{ top: number, left: number }} pos
 * @param {{ width: number, height: number }} tooltipSize
 */
function fitsInViewport(pos, tooltipSize) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const insets = getWalkthroughViewportInsets();
  return (
    pos.left >= insets.left &&
    pos.top >= insets.top &&
    pos.left + tooltipSize.width <= vw - insets.right &&
    pos.top + tooltipSize.height <= vh - insets.bottom
  );
}

/**
 * @param {DOMRect} targetRect
 * @param {{ width: number, height: number }} tooltipSize
 * @param {'top' | 'bottom' | 'left' | 'right'} preferred
 * @returns {{ top: number, left: number, placement: 'top' | 'bottom' | 'left' | 'right' }}
 */
export function computeTooltipPosition(targetRect, tooltipSize, preferred) {
  const insets = getWalkthroughViewportInsets();
  const centerX = targetRect.left + targetRect.width / 2 - tooltipSize.width / 2;
  const centerY = targetRect.top + targetRect.height / 2 - tooltipSize.height / 2;
  const clampedX = Math.min(
    Math.max(centerX, insets.left),
    window.innerWidth - tooltipSize.width - insets.right
  );
  const clampedY = Math.min(
    Math.max(centerY, insets.top),
    window.innerHeight - tooltipSize.height - insets.bottom
  );

  const candidates = {
    bottom: {
      top: targetRect.bottom + TOOLTIP_GAP,
      left: clampedX,
      placement: 'bottom',
    },
    top: {
      top: targetRect.top - TOOLTIP_GAP - tooltipSize.height,
      left: clampedX,
      placement: 'top',
    },
    right: {
      top: clampedY,
      left: targetRect.right + TOOLTIP_GAP,
      placement: 'right',
    },
    left: {
      top: clampedY,
      left: targetRect.left - TOOLTIP_GAP - tooltipSize.width,
      placement: 'left',
    },
  };

  for (const key of placementFallbackOrder(preferred)) {
    const pos = candidates[key];
    if (fitsInViewport(pos, tooltipSize)) {
      return pos;
    }
  }

  return {
    top: clampedY,
    left: clampedX,
    placement: preferred,
  };
}

export { VIEWPORT_PADDING, TOOLTIP_GAP };
